import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from './RootStore';

export interface CachedReferenceOption {
  id: string;
  displayId: string;
  [key: string]: any;
}

interface CacheEntry {
  rawData: any[];  // Complete entity data
  loadedAt: number;
  status: 'loading' | 'loaded' | 'error';
}

export interface CacheableEntityConfig {
  entityName: string;
  apiListMethod: string;
  getValuesFunc: (item: any, langStore: any) => CachedReferenceOption;
  orderBy?: any[];
}

export class ReferenceCacheStore {
  private cache = new Map<string, CacheEntry>();
  private pendingLoads = new Map<string, Promise<any[]>>();
  private cacheableEntities: Map<string, CacheableEntityConfig>;

  constructor(
    private rootStore: RootStore,
    cacheableEntityConfigs: CacheableEntityConfig[]
  ) {
    this.cacheableEntities = new Map(
      cacheableEntityConfigs.map(c => [c.entityName, c])
    );
    makeAutoObservable<ReferenceCacheStore, 'cacheableEntities' | 'pendingLoads'>(this, {
      cacheableEntities: false,
      pendingLoads: false,
    });
  }

  isCacheable(entityName: string): boolean {
    return this.cacheableEntities.has(entityName);
  }

  /**
   * Get transformed options for dropdowns (applies getValuesFunc)
   */
  getOptions(entityName: string): CachedReferenceOption[] {
    const entry = this.cache.get(entityName);
    if (!entry || entry.status !== 'loaded') {
      this.ensureLoaded(entityName);
      return [];
    }
    const config = this.cacheableEntities.get(entityName);
    if (!config) return [];
    return entry.rawData.map(item => config.getValuesFunc(item, this.rootStore.langStore));
  }

  /**
   * Get raw/complete entity data for listings
   */
  getRawData(entityName: string): any[] {
    const entry = this.cache.get(entityName);
    if (!entry || entry.status !== 'loaded') {
      return [];
    }
    return entry.rawData;
  }

  getFilteredOptions(entityName: string, searchValue: string): CachedReferenceOption[] {
    const options = this.getOptions(entityName);
    if (!searchValue) return options;
    const lowerSearch = searchValue.toLowerCase();
    return options.filter(opt =>
      opt.displayId.toLowerCase().includes(lowerSearch)
    );
  }

  isLoading(entityName: string): boolean {
    return this.cache.get(entityName)?.status === 'loading';
  }

  isLoaded(entityName: string): boolean {
    return this.cache.get(entityName)?.status === 'loaded';
  }

  async ensureLoaded(entityName: string): Promise<any[]> {
    const config = this.cacheableEntities.get(entityName);
    if (!config) {
      console.warn(`[ReferenceCacheStore] Entity "${entityName}" is not configured as cacheable`);
      return [];
    }

    const existing = this.cache.get(entityName);
    if (existing?.status === 'loaded') {
      return existing.rawData;
    }

    const pending = this.pendingLoads.get(entityName);
    if (pending) {
      return pending;
    }

    runInAction(() => {
      this.cache.set(entityName, {
        rawData: [],
        loadedAt: 0,
        status: 'loading',
      });
    });

    const loadPromise = this.loadEntity(entityName, config);
    this.pendingLoads.set(entityName, loadPromise);

    try {
      const rawData = await loadPromise;
      return rawData;
    } finally {
      this.pendingLoads.delete(entityName);
    }
  }

  private async loadEntity(
    entityName: string,
    config: CacheableEntityConfig
  ): Promise<any[]> {
    try {
      const { getApiAsync } = await import('../api');
      const api: any = await getApiAsync(this.rootStore.authStore, this.rootStore.activityStore);

      const params: any = {
        offset: 0,
        take: 10000,
      };

      if (config.orderBy) {
        params.orderBy = config.orderBy;
      }

      const result = await api[config.apiListMethod](params);
      const items = result[config.apiListMethod] || [];

      runInAction(() => {
        this.cache.set(entityName, {
          rawData: items,
          loadedAt: Date.now(),
          status: 'loaded',
        });
      });

      return items;
    } catch (error) {
      if (error instanceof Error && error.message === 'SESSION_EXPIRED') {
        console.warn(`[ReferenceCacheStore] No valid token for loading ${entityName}`);
        runInAction(() => {
          this.cache.set(entityName, {
            rawData: [],
            loadedAt: Date.now(),
            status: 'error',
          });
        });
        return [];
      }
      console.error(`[ReferenceCacheStore] Failed to load ${entityName}:`, error);

      runInAction(() => {
        this.cache.set(entityName, {
          rawData: [],
          loadedAt: Date.now(),
          status: 'error',
        });
      });

      return [];
    }
  }

  invalidate(entityName: string): void {
    this.cache.delete(entityName);
    this.pendingLoads.delete(entityName);
  }

  invalidateAll(): void {
    this.cache.clear();
    this.pendingLoads.clear();
  }

  getCacheStats(): {
    entityName: string;
    rows: number;
    sizeKB: number;
    cachedAt: number;
    status: 'loading' | 'loaded' | 'error' | 'not_loaded';
  }[] {
    const stats: {
      entityName: string;
      rows: number;
      sizeKB: number;
      cachedAt: number;
      status: 'loading' | 'loaded' | 'error' | 'not_loaded';
    }[] = [];

    for (const [entityName] of this.cacheableEntities) {
      const entry = this.cache.get(entityName);
      if (entry) {
        const sizeBytes = JSON.stringify(entry.rawData).length;
        stats.push({
          entityName,
          rows: entry.rawData.length,
          sizeKB: Math.round((sizeBytes / 1024) * 100) / 100,
          cachedAt: entry.loadedAt,
          status: entry.status,
        });
      } else {
        stats.push({
          entityName,
          rows: 0,
          sizeKB: 0,
          cachedAt: 0,
          status: 'not_loaded',
        });
      }
    }

    return stats;
  }
}
